import { from } from 'rxjs'
import moment from 'moment'
import config from '../config.json'
import { backendUrl } from '../consts'

const tokenKey = '@token'
const userOnboardKey = '@userOnboardKey'
const usernamedKey = '@usernamedKey'

export const isLoggedIn = () => {
  return !!localStorage.getItem(tokenKey)
}

export const getToken = () => {
  return localStorage.getItem(tokenKey)
}

export const logOut = () => {
  localStorage.removeItem(tokenKey)
}

const checkStatus = async res => {
  if (res.ok) {
    // res.status >= 200 && res.status < 300
    return res
  } else {
    if (res.status === 401) {
      removeAuthToken()
      // refresh in order to logout
      // eslint-disable-next-line no-restricted-globals
      location.reload()
    }

    if (+res.headers.get('content-length')) {
      const body = await res.json()
      throw {
        body,
        status: res.status,
        toString: function () {
          // TODO make a clearer error interface with server
          return this.body.non_field_errors?.join(', ') || this.body.detail
        }
      }
    }

    throw {
      status: res.status,
      toString: function () {
        return this.status
      }
    }
  }
}

// For logging out unmark this (then remark to avoid keeping on loggin out)
// Note that due to chaning app navigator mechanism you will need to reopen the app
// after unmarking this
//localStorage.removeItem(tokenKey)

export const getHeaders = isPublic => {
  let headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }
  if (isPublic) {
    return new Promise(resolve => {
      resolve(headers)
    })
  }

  const data = localStorage.getItem(tokenKey)
  const token = data ? JSON.parse(data)?.token : ''

  if (token) headers = { ...headers, Authorization: `JWT ${token}` }

  return new Promise(resolve => {
    resolve(headers)
  })
}

export const getHeadersWithToken = token => {
  let headers = {
    Accept: 'application/json',
    'Content-Type': 'application/json'
  }

  if (token) headers = { ...headers, Authorization: `JWT ${token}` }

  return new Promise(resolve => {
    resolve(headers)
  })
}

export const removeAuthToken = () => {
  localStorage.removeItem('isLocationVerified')
  localStorage.removeItem('locationData')
  localStorage.removeItem('welcomeHmoDisplayed')
  return localStorage.removeItem(tokenKey)
}

export const setAuthToken = async ({ token }) => {
  if (!token) {
    return
  }

  const expiry = moment().add(config.tokenExpiryInSeconds, 's').toDate()

  const tokenData = {
    token,
    expiry
  }

  return await localStorage.setItem(tokenKey, JSON.stringify(tokenData))
}

export const setUsername = async ({ lowerEmail }) => {
  return await localStorage.setItem(usernamedKey, lowerEmail)
}

export const getUsername = () => {
  return from(_internalGetUsername())
}

const _internalGetUsername = async () => {
  const username = await localStorage.getItem(usernamedKey)
  return username
}

export const setUserOnBoarded = async () => {
  return await localStorage.setItem(userOnboardKey, 'true')
}

export const hasOnBoarded = () => {
  return from(_internalOnboardedCheck())
}

const _internalOnboardedCheck = async () => {
  const userOnboarded = await localStorage.getItem(userOnboardKey)
  if (!userOnboarded && !JSON.parse(userOnboarded)) {
    return false
  }
  return true
}

const _internalTokenCheck = async () => {
  const data = await localStorage.getItem(tokenKey)

  if (!data) {
    return false
  }

  const tokenData = JSON.parse(data)

  return tokenData.token && new Date() < new Date(tokenData.expiry)
}

export const authTokenExists = async () => {
  const data = await localStorage.getItem(tokenKey)

  if (!data) {
    return false
  }
  return true
}

export const hasAuthToken = () => {
  return from(_internalTokenCheck())
}

const sendWithBody = (path, bodyObj, method = 'POST', baseUrl) => {
  const body = JSON.stringify(bodyObj)
  const url = `${baseUrl ? baseUrl : backendUrl}${path}`

  return from(
    getHeaders()
      .then(headers => {
        return fetch(url, {
          headers,
          body,
          method
        })
      })
      .then(checkStatus)
      .then(res => {
        return +res.headers.get('content-length') ? res?.json() : res?.text()
      })
  )
}

const toBlob = res => {
  return res.blob().then(blob => {
    return URL.createObjectURL(blob)
  })
}

export const getPDF = (path, baseUrl = backendUrl, token) => {
  const url = `${baseUrl}${path}`

  return from(
    (token ? getHeadersWithToken(token) : getHeaders())
      .then(headers => {
        return fetch(url, {
          headers
        })
      })
      .then(checkStatus)
      .then(res =>
        +res.headers.get('content-length') ? toBlob(res) : res.text()
      )
  )
}

export const postPDF = (path, bodyObj) => {
  const body = JSON.stringify(bodyObj)
  const url = `${backendUrl}${path}`

  return from(
    getHeaders()
      .then(headers => {
        return fetch(url, {
          headers,
          body,
          method: 'POST'
        })
      })
      .then(checkStatus)
      .then(res =>
        +res.headers.get('content-length') ? toBlob(res) : res.text()
      )
  )
}

export const putFormData = (path, data) => {
  const url = `${backendUrl}${path}`

  const formData = new FormData()

  formData.append('patient_id', data.patient_id)
  formData.append('name', data.name)
  formData.append('type', data.type)
  formData.append('file', data.content)

  return from(
    getHeaders()
      .then(({ Authorization }) => {
        return fetch(url, {
          method: 'PUT',
          headers: { Authorization },
          body: formData
        })
      })
      .then(checkStatus)
      .then(res => {
        // Parse JSON if there's content, otherwise return empty object
        return +res.headers.get('content-length') ? res.json() : {}
      })
  )
}

export const postJSON = (path, bodyObj) => {
  return sendWithBody(path, bodyObj, 'POST')
}

export const putJSON = (path, bodyObj) => {
  return sendWithBody(path, bodyObj, 'PUT')
}

export const patchJSON = (path, bodyObj, baseUrl = backendUrl) => {
  return sendWithBody(path, bodyObj, 'PATCH', baseUrl)
}

export const getJSON = (path, baseUrl = backendUrl, isPublic = false) => {
  const url = `${baseUrl}${path}`
  return from(
    getHeaders(isPublic)
      .then(headers => {
        return fetch(url, {
          headers
        })
      })
      .then(checkStatus)
      .then(res => res.json())
  )
}

export const getJSONToken = (path, baseUrl = backendUrl, token) => {
  const url = `${baseUrl}${path}`
  return from(
    getHeadersWithToken(token)
      .then(headers => {
        return fetch(url, {
          headers
        })
      })
      .then(checkStatus)
      .then(res => res.json())
  )
}

export const getCallToken = (userId, roomName) => {
  // token is not used anymore in the code
  // so I removed the call to the server
  return from(Promise.resolve())
}

export const pingCallServer = userId => {
  console.log('pingCallServer marked for deprication and will be removed')
  // use postCallStateUpdate instead secure_video_leftover
  const url = `${backendUrl}/device/state_update?id=p_${userId}`

  return from(
    getHeaders()
      .then(headers => {
        return fetch(url, {
          headers
        })
      })
      .then(checkStatus)
  )
}

export const postCallStateUpdate = userId => {
  return postJSON(`/video-calls/state-update`, {
    identity: `p_${userId}`
  })
}

export const checkForPendingRooms = userId => {
  if (authTokenExists()) {
    return getJSON(`/device/pending_rooms?id=p_${userId}`)
  }
  return new Promise(resolve => {
    resolve({})
  })
}

export const getLocaleFromLanguage = (language, languages) => {
  return languages?.[language]?.locale
}

export const getLanguageFromLocale = (locale, languages) => {
  if (!languages) return null
  return Object.keys(languages)?.find(key => languages[key]?.locale === locale)
}
